import { create } from 'zustand';
import type RemoteStorage from 'remotestoragejs';
import { format } from 'date-fns';
import { TodoItem, SortOption } from '@/types';
import { serializeTodo, sortTodos } from '@/lib/utils/todo';
import { determineAction } from '@/app/actions';
import { useUIStore } from './use-ui-store';
import { logAction, logTodoAction, LogActions } from '@/lib/utils/logging';

export type ViewMode = 'daily' | 'archive';

interface TodoStore {
  // State
  todos: TodoItem[];
  selectedDate: Date;
  sortBy: SortOption;
  viewMode: ViewMode;

  // RemoteStorage config
  remoteStorage: RemoteStorage | null;
  apiKey: string;

  // Internal refs (for managing async operations)
  isInitialized: boolean;
  isSaving: boolean;
  isLoading: boolean;

  // Setters
  setRemoteStorage: (rs: RemoteStorage | null) => void;
  setApiKey: (key: string) => void;
  setSelectedDate: (date: Date) => void;
  setSortBy: (option: SortOption) => void;
  setViewMode: (mode: ViewMode) => void;

  // Todo CRUD operations
  addTodo: (todo: TodoItem) => Promise<void>;
  updateTodo: (todo: TodoItem) => Promise<void>;
  deleteTodo: (id: string) => void;
  unarchiveTodo: (id: string) => Promise<void>;
  toggleTodo: (id: string) => void;
  setAllTodos: (todos: TodoItem[]) => Promise<void>;

  // Bulk operations
  clearAllTodos: () => void;
  clearCompletedTodos: () => void;
  clearIncompleteTodos: () => void;

  // RemoteStorage operations
  loadTodos: () => Promise<void>;

  // AI actions
  handleAction: (text: string, emoji: string) => Promise<void>;

  // Selectors (computed)
  getTodosForDate: (date?: Date) => TodoItem[];
  getSortedTodos: (date?: Date) => TodoItem[];
  getDatesWithTodos: () => Set<string>;
  getArchivedTodos: () => TodoItem[];
}

export const useTodoStore = create<TodoStore>((set, get) => ({
  // Initial state
  todos: [],
  selectedDate: new Date(),
  sortBy: 'newest',
  viewMode: 'daily',
  remoteStorage: null,
  apiKey: '',
  isInitialized: false,
  isSaving: false,
  isLoading: false,

  // Setters
  setRemoteStorage: (rs) => {
    set({ remoteStorage: rs });
    if (rs) {
      // Set up change listener for external changes
      rs.onChange('/todonna/', () => {
        const { isInitialized, isSaving, isLoading } = get();
        // Don't reload if we're currently saving (prevents loops)
        if (isInitialized && !isSaving && !isLoading) {
          console.log('RemoteStorage changed externally, reloading...');
          get().loadTodos();
        }
      });

      // Initial load
      get().loadTodos();
    }
  },

  setApiKey: (key) => set({ apiKey: key }),

  setSelectedDate: async (date) => {
    const { remoteStorage } = get();
    set({ selectedDate: date });
    // Log date change
    await logAction(remoteStorage, LogActions.CHANGE_DATE, {
      date: date.toISOString(),
    });
  },

  setSortBy: async (option) => {
    const { remoteStorage } = get();
    set({ sortBy: option });
    // Log sort change
    await logAction(remoteStorage, LogActions.CHANGE_SORT, { sortBy: option });
  },

  setViewMode: async (mode) => {
    const { remoteStorage } = get();
    set({ viewMode: mode });
    // Log view change
    await logAction(remoteStorage, LogActions.CHANGE_VIEW, { viewMode: mode });
  },

  // Load todos from RemoteStorage using Todonna module
  loadTodos: async () => {
    const { remoteStorage, isLoading: currentlyLoading } = get();

    if (!remoteStorage || currentlyLoading) return;

    set({ isLoading: true });

    // Only show loading indicator for initial load
    const isInitialized = get().isInitialized;
    if (!isInitialized) {
      useUIStore.getState().setIsLoadingTodonna(true);
    }

    try {
      // Use the Todonna module API
      const todos = await remoteStorage.todonna.getAll();
      
      console.log(`Loaded ${todos.length} todos from RemoteStorage`);
      set({ todos, isInitialized: true, isLoading: false });
      useUIStore.getState().setIsLoadingTodonna(false);
    } catch (error) {
      console.error('Failed to load todos:', error);
      set({ todos: [], isInitialized: true, isLoading: false });
      useUIStore.getState().setIsLoadingTodonna(false);
    }
  },

  // Add a new todo using Todonna module
  addTodo: async (todo: TodoItem) => {
    const { remoteStorage, todos } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });

    try {
      // Optimistically update local state first
      set({ todos: [...todos, serializeTodo(todo)] });

      // Use Todonna module API
      await remoteStorage.todonna.add(todo);

      // Log the action
      await logTodoAction(remoteStorage, LogActions.ADD_TODO, todo.id, {
        text: todo.text,
        emoji: todo.emoji,
        date: todo.date.toISOString(),
        time: todo.time,
      });

      console.log(`Added todo: ${todo.text}`);
    } catch (error) {
      console.error('Failed to add todo:', error);
      // Rollback on error
      set({ todos: todos.filter((t) => t.id !== todo.id) });
    } finally {
      set({ isSaving: false });
    }
  },

  // Update an existing todo using Todonna module
  updateTodo: async (todo: TodoItem) => {
    const { remoteStorage, todos } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });
    const previousTodos = todos;

    try {
      // Optimistically update local state first
      set({
        todos: todos.map((t) => (t.id === todo.id ? serializeTodo(todo) : t)),
      });

      // Use Todonna module API
      await remoteStorage.todonna.update(todo.id, {
        text: todo.text,
        todo_item_id: todo.todo_item_id,
        todo_item_status: todo.todo_item_status,
        emoji: todo.emoji,
        date: todo.date,
        time: todo.time,
        removed: todo.removed,
      });

      // Log the action
      await logTodoAction(remoteStorage, LogActions.UPDATE_TODO, todo.id, {
        text: todo.text,
        todo_item_status: todo.todo_item_status,
        emoji: todo.emoji,
        date: todo.date.toISOString(),
        time: todo.time,
        removed: todo.removed,
      });

      console.log(`Updated todo: ${todo.text}`);
    } catch (error) {
      console.error('Failed to update todo:', error);
      // Rollback on error
      set({ todos: previousTodos });
    } finally {
      set({ isSaving: false });
    }
  },

  // Delete a todo (soft delete by updating with removed flag)
  deleteTodo: async (id: string) => {
    const { remoteStorage } = get();
    const todo = get().todos.find((t) => t.id === id);
    if (todo) {
      await get().updateTodo({ ...todo, removed: true });
      // Log the delete action
      await logTodoAction(remoteStorage, LogActions.DELETE_TODO, id, {
        text: todo.text,
      });
    }
  },

  // Unarchive a todo (restore by updating with removed flag set to false)
  unarchiveTodo: async (id: string) => {
    const { remoteStorage } = get();
    const todo = get().todos.find((t) => t.id === id);
    if (todo) {
      await get().updateTodo({ ...todo, removed: false });
      // Log the unarchive action
      await logTodoAction(remoteStorage, LogActions.UNARCHIVE_TODO, id, {
        text: todo.text,
      });
    }
  },

  // Toggle todo completion
  toggleTodo: async (id: string) => {
    const { remoteStorage } = get();
    const todo = get().todos.find((t) => t.id === id);
    if (todo) {
      // Toggle between pending and done
      const newStatus = todo.todo_item_status === "done" ? "pending" : "done";
      await get().updateTodo({ ...todo, todo_item_status: newStatus });
      // Log the toggle action
      await logTodoAction(remoteStorage, LogActions.TOGGLE_TODO, id, {
        todo_item_status: newStatus,
        text: todo.text,
      });
    }
  },

  // Bulk update todos using Todonna module
  setAllTodos: async (newTodos: TodoItem[]) => {
    const { remoteStorage, todos } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });
    const previousTodos = todos;

    try {
      // Optimistically update local state first
      set({ todos: newTodos.filter((t) => !t.removed).map(serializeTodo) });

      // Use Todonna module's replaceAll for efficient bulk operations
      await remoteStorage.todonna.replaceAll(newTodos.filter((t) => !t.removed));

      console.log(`Bulk updated ${newTodos.length} todos`);
    } catch (error) {
      console.error('Failed to bulk update todos:', error);
      // Rollback on error
      set({ todos: previousTodos });
    } finally {
      set({ isSaving: false });
    }
  },

  // Bulk operations using Todonna module
  clearAllTodos: async () => {
    const { remoteStorage, selectedDate } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });

    try {
      await remoteStorage.todonna.clearByDate(selectedDate);
      // Log the action
      await logAction(remoteStorage, LogActions.CLEAR_ALL_TODOS, {
        date: selectedDate.toISOString(),
      });
      // Reload to sync state
      await get().loadTodos();
    } catch (error) {
      console.error('Failed to clear todos:', error);
    } finally {
      set({ isSaving: false });
    }
  },

  clearCompletedTodos: async () => {
    const { remoteStorage, selectedDate } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });

    try {
      await remoteStorage.todonna.clearCompletedByDate(selectedDate);
      // Log the action
      await logAction(remoteStorage, LogActions.CLEAR_COMPLETED_TODOS, {
        date: selectedDate.toISOString(),
      });
      // Reload to sync state
      await get().loadTodos();
    } catch (error) {
      console.error('Failed to clear completed todos:', error);
    } finally {
      set({ isSaving: false });
    }
  },

  clearIncompleteTodos: async () => {
    const { remoteStorage, selectedDate } = get();
    if (!remoteStorage) return;

    set({ isSaving: true });

    try {
      await remoteStorage.todonna.clearIncompleteByDate(selectedDate);
      // Log the action
      await logAction(remoteStorage, LogActions.CLEAR_INCOMPLETE_TODOS, {
        date: selectedDate.toISOString(),
      });
      // Reload to sync state
      await get().loadTodos();
    } catch (error) {
      console.error('Failed to clear incomplete todos:', error);
    } finally {
      set({ isSaving: false });
    }
  },

  // AI action handler
  handleAction: async (text: string, emoji: string) => {
    if (!text.trim()) return;

    const { todos, selectedDate, apiKey, remoteStorage } = get();

    useUIStore.getState().setIsLoading(true);

    let newTodos = [...todos];

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Filter todos for the selected date
      const filteredTodos = todos.filter((todo) => {
        const todoDate = new Date(todo.date);
        return (
          todoDate.toDateString() === selectedDate.toDateString() &&
          !todo.removed
        );
      });

      const actions = (
        await determineAction(
          text,
          emoji || '',
          filteredTodos,
          'vif-openai',
          timezone,
          apiKey
        )
      ).actions;

      for (const action of actions) {
        switch (action.action) {
          case 'add':
            const todoDate = action.targetDate
              ? new Date(action.targetDate)
              : selectedDate;
            const newTodo = serializeTodo({
              id: `id${Math.random().toString(36).substring(2)}`,
              text: action.text || text,
              todo_item_status: "pending",
              emoji: action.emoji || emoji,
              date: todoDate,
              time: action.time,
            });
            newTodos.push(newTodo);
            break;

          case 'delete':
            if (action.todoId) {
              get().deleteTodo(action.todoId);
            }
            break;

          case 'mark':
            if (action.todoId) {
              newTodos = newTodos.map((todo) =>
                todo.id === action.todoId
                  ? {
                      ...todo,
                      todo_item_status:
                        action.status === 'done'
                          ? 'done'
                          : action.status === 'pending'
                          ? 'pending'
                          : action.status === 'archived'
                          ? 'archived'
                          : todo.todo_item_status === 'done'
                          ? 'pending'
                          : 'done',
                    }
                  : todo
              );
            }
            break;

          case 'sort':
            if (action.sortBy) {
              set({ sortBy: action.sortBy });
            }
            break;

          case 'edit':
            if (action.todoId && action.text) {
              const todo = todos.find((t) => t.id === action.todoId);
              if (todo) {
                const updated = serializeTodo({
                  ...todo,
                  text: action.text,
                  emoji: action.emoji || todo.emoji,
                  date: action.targetDate ? new Date(action.targetDate) : todo.date,
                  time: action.time || todo.time,
                  todo_item_status: action.status || todo.todo_item_status,
                });
                await get().updateTodo(updated);
              }
            }
            break;

          case 'clear':
            if (action.listToClear === 'all') {
              await get().clearAllTodos();
            } else if (action.listToClear === 'done') {
              await get().clearCompletedTodos();
            } else if (action.listToClear === 'pending') {
              await get().clearIncompleteTodos();
            }
            break;
        }
      }

      await get().setAllTodos(newTodos);
      
      // Log successful AI action
      await logAction(remoteStorage, LogActions.AI_ACTION, {
        text,
        emoji,
        actionCount: actions.length,
        actions: actions.map(a => a.action),
      });
    } catch (error) {
      console.error('AI Action failed:', error);
      // Log failed AI action
      await logAction(remoteStorage, LogActions.AI_ACTION_FAILED, {
        text,
        error: error instanceof Error ? error.message : String(error),
      });
      // Fallback: just add the todo as-is
      const fallbackTodo = serializeTodo({
        id: `id${Math.random().toString(36).substring(2)}`,
        text,
        todo_item_status: "pending",
        emoji,
        date: selectedDate,
      });
      await get().addTodo(fallbackTodo);
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  // Selectors
  getTodosForDate: (date?: Date) => {
    const { todos, selectedDate, viewMode } = get();
    const targetDate = date || selectedDate;

    return todos.filter((todo) => {
      const todoDate = new Date(todo.date);
      const dateMatches = todoDate.toDateString() === targetDate.toDateString();
      
      if (viewMode === 'archive') {
        return dateMatches && todo.removed === true;
      }
      
      return dateMatches && !todo.removed;
    });
  },
  
  getArchivedTodos: () => {
    const { todos } = get();
    // Get all archived todos, sort by date (newest first), and limit to 50
    return todos
      .filter((todo) => todo.removed === true)
      .sort((a, b) => {
        // Sort by date, most recent first
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      })
      .slice(0, 50);
  },

  getSortedTodos: (date?: Date) => {
    const { sortBy } = get();
    const filteredTodos = get().getTodosForDate(date);
    return sortTodos(filteredTodos, sortBy);
  },

  getDatesWithTodos: () => {
    const { todos } = get();
    const dates = new Set<string>();

    todos.forEach((todo) => {
      if (!todo.removed) {
        const dateStr = format(new Date(todo.date), 'yyyy-MM-dd');
        dates.add(dateStr);
      }
    });

    return dates;
  },
}));
